import React, { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import isFunction from 'lodash/isFunction';

import {
  AlertIcon,
  InfoIcon,
  IssueClosedIcon,
  XCircleIcon,
} from '@primer/octicons-react';
import { StyledOcticon } from '@primer/react';

import Button from '../Button/Button';

export const MESSAGE_STATES = {
  INFO: 'info',
  WARNING: 'warning',
  ERROR: 'error',
  READY: 'ready',
};

const getStateIcon = (messageState) => {
  switch (messageState) {
  case MESSAGE_STATES.ERROR:
    return XCircleIcon;
  case MESSAGE_STATES.WARNING:
    return AlertIcon;
  case MESSAGE_STATES.READY:
    return IssueClosedIcon;
  default:
    return InfoIcon;
  }
}

const getHoverTextClassByState = (messageState) => {
  switch (messageState) {
    case MESSAGE_STATES.ERROR:
      return 'hover:text-banner-faded-error';
    case MESSAGE_STATES.WARNING:
      return 'hover:text-banner-faded-warning';
    case MESSAGE_STATES.READY:
      return 'hover:text-banner-faded-success';
    default:
      return 'hover:text-banner-faded-info';
  }
}

const getFadedClassByState = (messageState) => {
  switch (messageState) {
  case MESSAGE_STATES.ERROR:
    return 'hover:text-banner-faded-error bg-banner-faded-error';
  case MESSAGE_STATES.WARNING:
    return 'hover:text-banner-faded-warning bg-banner-faded-warning';
  case MESSAGE_STATES.READY:
    return 'hover:text-banner-faded-success bg-banner-faded-success';
  default:
    return 'hover:text-banner-faded-info bg-banner-faded-info';
  }
}

export const StatusBanner = ({
  className,
  message,
  title,
  onClose,
  icon,
  open = true,
  status = MESSAGE_STATES.INFO
}) => {
  const [isOpen, setOpen] = useState(open);
  const [inOpeningAnimation, setInOpeningAnimation] = useState(false);
  const [inClosingAnimation, setInClosingAnimation] = useState(false);

  const handleBannerClose = (e) => {
    if (isFunction(onClose)) {
      onClose(e);
    }
    setInClosingAnimation(false);
    setOpen(false);
  }

  const handleAnimationEnd = (e) => {
    if (inClosingAnimation) {
      handleBannerClose(e);
    }
    if (inOpeningAnimation) {
      setInOpeningAnimation(false);
    }
  };

  const DisplayIcon = useMemo(() => {
    if (icon) {
      return icon;
    }

    return getStateIcon(status);
  }, [icon, status]);

  useEffect(() => {
    if (isOpen) {
      setInOpeningAnimation(true)
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen && open) {
      setOpen(open);
    }
  }, [open, isOpen])

  if (!isOpen) {
    return null;
  }

  const hoverTextColor = getHoverTextClassByState(status);
  return (
    <div
      className={classNames('flex flex-row justify-between items-center origin-top-left pl-6 pr-4 py-4 border border-border-dark rounded shadow-none',
        'text-black border-black',
        getFadedClassByState(status),
        { 'animate-retract': inClosingAnimation },
        { 'animate-unretract': inOpeningAnimation },
        className)}
      onAnimationEnd={handleAnimationEnd}
    >
      <div className="flex flex-row justify-between items-center">
        <StyledOcticon
          className="text-black"
          icon={DisplayIcon}
          size="medium"
        />
        <div className="flex flex-col justify-between ml-4">
          <span className="font-bold text-lg text-black">
            { title }
          </span>
          <span className="mt-1 text-black">
            { message }
          </span>
        </div>
      </div>
      <Button
        variant="close"
        className={classNames('text-black', hoverTextColor)}
        onClick={() => setInClosingAnimation(true)}
      />
    </div>
  )
}

export default StatusBanner;
